/**
 * CetusEd Admin Console — Billing Dashboard
 *
 * COMPLIANCE: FERPA does not directly regulate billing data, but billing
 *   records are subject to audit and may be requested under state public
 *   records laws. All billing data must be retained per the district's
 *   record retention policy.
 */

import React from 'react';
import { useBillingSummary } from '../../api/hooks';
import { Link } from 'react-router-dom';

export const BillingDashboard: React.FC = () => {
  const { data: summaries, isLoading, error } = useBillingSummary();

  if (isLoading) return <div className="admin-loading">Loading billing data...</div>;

  if (error) {
    return (
      <div className="admin-error" role="alert">
        Unable to load billing summary.
      </div>
    );
  }

  const totalMonthly = summaries?.reduce((sum, s) => sum + s.totalCost, 0) || 0;
  const activeCount = summaries?.filter((s) => s.status === 'active' || s.status === 'trialing').length || 0;

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Billing Dashboard</h1>
        <div className="header-actions">
          <Link to="/billing/invoices" className="admin-btn admin-btn--secondary">Invoices</Link>
          <Link to="/billing/plans" className="admin-btn admin-btn--secondary">Plans</Link>
        </div>
      </div>

      <div className="metric-cards">
        <div className="metric-card">
          <span className="metric-value">${totalMonthly.toLocaleString()}</span>
          <span className="metric-label">Monthly Revenue</span>
        </div>
        <div className="metric-card">
          <span className="metric-value">{activeCount}</span>
          <span className="metric-label">Active Districts</span>
        </div>
        <div className="metric-card">
          <span className="metric-value">{summaries?.length || 0}</span>
          <span className="metric-label">Total Districts</span>
        </div>
      </div>

      {summaries && summaries.length > 0 && (
        <table className="data-table" role="table">
          <thead>
            <tr>
              <th scope="col">District</th>
              <th scope="col">Plan</th>
              <th scope="col">Status</th>
              <th scope="col">Monthly</th>
              <th scope="col">Period</th>
            </tr>
          </thead>
          <tbody>
            {summaries.map((s) => (
              <tr key={s.districtId}>
                <td>{s.districtName}</td>
                <td>{s.plan}</td>
                <td><span className={`status-badge status-badge--${s.status}`}>{s.status}</span></td>
                <td>${s.totalCost.toFixed(2)}</td>
                <td>
                  {new Date(s.currentPeriodStart).toLocaleDateString()}
                  {' — '}
                  {new Date(s.currentPeriodEnd).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
